import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  itemsPerPage?:
    | number
    | Partial<Record<"base" | "sm" | "md" | "lg" | "xl" | "2xl", number>>;
  className?: string;
  showLeftArrow?: boolean;
  showRightArrow?: boolean;
  arrowOverlay?: boolean;
  enableMobilePopout?: boolean;
  renderPopoutItem?: (item: T, index: number) => React.ReactNode;
}

function Carousel<T>({
  items,
  renderItem,
  itemsPerPage = 5,
  className = "",
  showLeftArrow = true,
  showRightArrow = true,
  arrowOverlay = false,
  enableMobilePopout = false,
  renderPopoutItem,
}: CarouselProps<T>) {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isMobileView, setIsMobileView] = useState(false);
  const [popoutItem, setPopoutItem] = useState<{
    item: T;
    index: number;
  } | null>(null);

  const breakpointPx = useMemo(
    () => ({ sm: 640, md: 768, lg: 1024, xl: 1280, "2xl": 1536 }),
    [],
  );

  const resolveItemsPerPage = (value: CarouselProps<T>["itemsPerPage"]) => {
    if (typeof value === "number") return value;
    // Default base to 1 if not specified when using responsive config
    let resolved = value?.base ?? 1;
    if (typeof window === "undefined") return resolved;
    const width = window.innerWidth;
    const order: Array<keyof typeof breakpointPx> = [
      "sm",
      "md",
      "lg",
      "xl",
      "2xl",
    ];
    for (const key of order) {
      const minWidth = breakpointPx[key];
      if (width >= minWidth && typeof value?.[key] === "number") {
        resolved = value[key] as number;
      }
    }
    return resolved;
  };

  const [resolvedItemsPerPage, setResolvedItemsPerPage] = useState<number>(() =>
    resolveItemsPerPage(itemsPerPage),
  );

  useEffect(() => {
    const handleResize = () => {
      const next = resolveItemsPerPage(itemsPerPage);
      const mobile = window.innerWidth < breakpointPx.md;
      setIsMobileView(mobile);
      if (!mobile) setPopoutItem(null);
      setResolvedItemsPerPage((prev) => {
        if (prev !== next) return next;
        return prev;
      });
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true } as any);
    return () => window.removeEventListener("resize", handleResize as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage]);

  useEffect(() => {
    if (!popoutItem) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPopoutItem(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [popoutItem]);

  // Clamp index when items/count changes
  useEffect(() => {
    const total = items.length;
    const maxIndex = Math.max(0, total - resolvedItemsPerPage);
    setCarouselIndex((current) => Math.min(current, maxIndex));
  }, [items.length, resolvedItemsPerPage]);

  const total = items.length;
  const isCarousel = total > resolvedItemsPerPage;
  const maxIndex = total - resolvedItemsPerPage;
  const visibleItems = isCarousel
    ? items.slice(carouselIndex, carouselIndex + resolvedItemsPerPage)
    : items;

  return (
    <div className={`flex items-center relative ${className}`}>
      {/* Left Arrow */}
      {isCarousel && showLeftArrow && (
        <button
          onClick={() => setCarouselIndex((i) => Math.max(0, i - 1))}
          disabled={carouselIndex === 0}
          className={`p-0.5 md:p-1 mr-1 md:mr-4 text-2xl font-bold transition-opacity shadow-lg rounded-full z-10 ${
            carouselIndex === 0 ? "opacity-0 cursor-default" : "opacity-100"
          } ${arrowOverlay ? "absolute left-2 top-1/2 -translate-y-1/2" : ""}`}
        >
          <ChevronLeft className="w-4 h-4 md:w-10 md:h-10" />
        </button>
      )}
      <div
        className="grid gap-2 flex-1"
        style={{ gridTemplateColumns: `repeat(${resolvedItemsPerPage}, 1fr)` }}
      >
        {visibleItems.map((item, idx) => {
          const itemIndex = carouselIndex + idx;
          const itemContent = renderItem(item, itemIndex);

          if (!enableMobilePopout || !isMobileView) {
            return (
              <React.Fragment key={itemIndex}>{itemContent}</React.Fragment>
            );
          }

          return (
            <button
              key={itemIndex}
              type="button"
              onClick={() => setPopoutItem({ item, index: itemIndex })}
              className="block w-full cursor-zoom-in text-left"
              aria-label="Open image preview"
            >
              {itemContent}
            </button>
          );
        })}
      </div>
      {/* Right Arrow */}
      {isCarousel && showRightArrow && (
        <button
          onClick={() => setCarouselIndex((i) => Math.min(maxIndex, i + 1))}
          disabled={carouselIndex >= maxIndex}
          className={` p-0.5 md:p-1 ml-1 md:ml-4 text-2xl font-bold transition-opacity shadow-lg rounded-full z-10 bg-white ${
            carouselIndex >= maxIndex
              ? "opacity-0 cursor-default"
              : "opacity-100"
          } ${arrowOverlay ? "absolute right-2 top-1/2 -translate-y-1/2" : ""}`}
        >
          <ChevronRight className="w-4 h-4 md:w-10 md:h-10" />
        </button>
      )}
      {enableMobilePopout && isMobileView && popoutItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 md:hidden"
          role="dialog"
          aria-modal="true"
          onClick={() => setPopoutItem(null)}
        >
          <button
            type="button"
            onClick={() => setPopoutItem(null)}
            className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-900 shadow-lg"
            aria-label="Close image preview"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="max-h-[88vh] w-full overflow-hidden bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            {renderPopoutItem
              ? renderPopoutItem(popoutItem.item, popoutItem.index)
              : renderItem(popoutItem.item, popoutItem.index)}
          </div>
        </div>
      )}
    </div>
  );
}

export default Carousel;
